import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faLightbulb,
  faHeart,
  faPrint,
  faPalette,
  faShieldHeart,
  faGem,
  faClock,
  faCheckCircle,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faTruck,
  faStar,
  faUsers,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';

function About() {
  // Animasyon kontrolcüleri
  const controlsHero = useAnimation();
  const controlsValues = useAnimation();
  const controlsProcess = useAnimation();
  const controlsContact = useAnimation();
  
  // Referanslar
  const refHero = useRef(null);
  const refValues = useRef(null);
  const refProcess = useRef(null);
  const refContact = useRef(null);
  
  // Görünürlük durumları
  const [heroVisible, setHeroVisible] = useState(false);
  const [valuesVisible, setValuesVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  
  // Scroll olayını dinle ve elementlerin görünürlüğünü kontrol et
  useEffect(() => {
    const checkVisibility = () => {
      const isElementInView = (el, offset = 100) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return (
          rect.top <= window.innerHeight - offset &&
          rect.bottom >= offset
        );
      };
      
      if (!heroVisible && isElementInView(refHero.current)) {
        setHeroVisible(true);
        controlsHero.start('visible');
      }
      
      if (!valuesVisible && isElementInView(refValues.current)) {
        setValuesVisible(true);
        controlsValues.start('visible');
      }
      
      if (!processVisible && isElementInView(refProcess.current)) {
        setProcessVisible(true);
        controlsProcess.start('visible');
      }
      
      if (!contactVisible && isElementInView(refContact.current)) {
        setContactVisible(true);
        controlsContact.start('visible');
      }
    };
    
    // İlk yükleme kontrolü
    checkVisibility();
    
    // Scroll olayını dinle
    window.addEventListener('scroll', checkVisibility);
    
    // Temizleme fonksiyonu
    return () => {
      window.removeEventListener('scroll', checkVisibility);
    };
  }, [heroVisible, valuesVisible, processVisible, contactVisible, controlsHero, controlsValues, controlsProcess, controlsContact]);

  // Animasyon varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const itemFadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const pulseHighlight = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const values = [
    {
      icon: faRocket,
      title: "Yenilikçi Yaklaşım",
      description: "En son teknolojileri kullanarak müşterilerimize en iyi deneyimi sunuyoruz. Sürekli gelişim ve yenilik bizim temel prensibimizdir."
    },
    {
      icon: faLightbulb,
      title: "Yaratıcı Çözümler",
      description: "Her müşterimiz için özel ve benzersiz tasarımlar üretiyoruz. Yaratıcılık, markamızın DNA'sında var olan bir değerdir."
    },
    {
      icon: faHeart,
      title: "Müşteri Odaklılık",
      description: "Müşteri memnuniyeti bizim en büyük önceliğimiz. Her adımda müşterilerimizin ihtiyaç ve beklentilerini merkeze alıyoruz."
    }
  ];

  const features = [
    {
      icon: faPrint,
      title: "Premium Baskı",
      description: "1440 DPI HD çözünürlük"
    },
    {
      icon: faPalette,
      title: "Canlı Renkler",
      description: "Solmayan ve uzun ömürlü"
    },
    {
      icon: faShieldHeart,
      title: "Kalite Garantisi",
      description: "2 yıl ürün garantisi"
    },
    {
      icon: faGem,
      title: "Premium Malzeme",
      description: "A+ kalite materyal"
    }
  ];

  const processSteps = [
    {
      step: 1,
      icon: faLightbulb,
      title: "Tasarım",
      description: "Profesyonel tasarımcılarımız tarafından özel tasarım"
    },
    {
      step: 2,
      icon: faPrint,
      title: "HD Baskı",
      description: "1440 DPI çözünürlükte premium baskı kalitesi"
    },
    {
      step: 3,
      icon: faCheckCircle,
      title: "Kalite Kontrol",
      description: "Detaylı kalite kontrol ve dayanıklılık testleri"
    },
    {
      step: 4,
      icon: faTruck,
      title: "Hızlı Teslimat",
      description: "2-3 iş günü içinde kapınızda"
    }
  ];
  
  const stats = [
    { icon: faStar, value: "1000+", label: "Mutlu Müşteri" },
    { icon: faUsers, value: "50+", label: "Takım Üyesi" },
    { icon: faHandshake, value: "25+", label: "İş Ortağı" }
  ];

  return (
    <div className="page-transition overflow-hidden">
      {/* Hero Section - Paralaks Efektli */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Paralaks Arka Plan */}
        <div className="absolute inset-0 z-0">
          {/* Arka plan için beyaz overlay */}
          <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 to-white/90 z-1"></div>
          {/* Arka plan resmi */}
          <div className="absolute inset-0 z-0" style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586075010923-2dd4570fb338?q=80&w=2787&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(3px) brightness(0.7) contrast(0.9)',
            transform: 'scale(1.1)',
            mixBlendMode: 'multiply'
          }}></div>
        </div>
        
        {/* Dekoratif Şekiller */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-amber-700 opacity-10 animate-blob animation-delay-4000"></div>
        
        <div className="container mx-auto px-4 relative z-10" ref={refHero}>
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial="hidden"
            animate={controlsHero}
            variants={fadeInUp}
          >
            {/* Üst Etiket - Minimal */}
            <motion.div
              className="inline-block mb-6"
              variants={scaleIn}
            >
              <span className="text-amber-600 font-medium text-sm tracking-wider uppercase">Yeni Nesil Telefon Kılıflarında Öncü Marka</span>
            </motion.div>

            {/* Hikayemiz Başlığı - Tamamen Yeni Minimal Tasarım */}
            <div className="mb-8">
              <h1 className="text-5xl md:text-6xl font-bold text-amber-800 inline-block" style={{
                textRendering: 'geometricPrecision',
                WebkitFontSmoothing: 'antialiased'
              }}>
                HİKAYEMİZ
              </h1>
              
              {/* Minimal Alt Çizgi */}
              <div className="relative">
                <motion.div 
                  className="h-1.5 bg-amber-500 w-32 mx-auto mt-3"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "140px", opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                ></motion.div>
              </div>
            </div>

            {/* Minimal Açıklama Metni */}
            <motion.div
              className="max-w-2xl mx-auto mb-16"
              variants={fadeInUp}
            >
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-6">
                2024 yılında teknoloji ve tasarımı birleştirerek çıktığımız bu yolda, 
                her bir ürünümüzü <span className="text-amber-700 font-medium">sanat eseri</span> olarak görüyoruz.
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                <span className="text-amber-700 font-medium">Yenilikçi ve modern</span> yaklaşımımızla 
                sektöre yeni bir soluk getirirken, telefonunuzu sadece korumakla kalmıyor, 
                ona <span className="text-amber-700 font-medium">benzersiz bir kimlik</span> kazandırıyoruz.
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-3 mt-8"
              variants={staggerContainer}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="p-4 rounded-lg bg-white shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                  variants={itemFadeIn}
                >
                  <div className="flex items-center">
                    <div className="relative mr-3 w-12 h-12 flex items-center justify-center">
                      <div className="absolute inset-0 bg-amber-50 rounded-lg"></div>
                      <FontAwesomeIcon icon={feature.icon} className="text-xl text-amber-700 relative z-10" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-slate-800 mb-1">{feature.title}</h3>
                      <p className="text-slate-600 text-xs">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* İstatistikler Bölümü */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-amber-700 to-amber-900 text-white mt-12 md:mt-0">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <FontAwesomeIcon icon={stat.icon} className="text-4xl text-amber-300 mb-4" />
                <h3 className="text-4xl font-bold mb-2">{stat.value}</h3>
                <p className="text-amber-100">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section - 3D Kartlar */}
      <section className="py-24 bg-slate-50 relative overflow-hidden" ref={refValues}>
        {/* Dekoratif Şekiller */}
        <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent"></div>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-200 opacity-30"></div>
        <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-amber-100 opacity-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center mb-20"
            initial="hidden"
            animate={controlsValues}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">NEDEN BİZ?</span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6">
              Değerlerimiz
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Yenilikçi ve müşteri odaklı yaklaşımımızla, 
              her zaman en iyisini sunmaya çalışıyoruz.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-10"
            initial="hidden"
            animate={controlsValues}
            variants={staggerContainer}
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group perspective"
                variants={itemFadeIn}
                whileHover={{ rotateY: 5, rotateX: -5 }}
              >
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 w-20 h-20 rounded-2xl flex items-center justify-center mb-8 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <FontAwesomeIcon 
                    icon={value.icon} 
                    className="text-4xl text-amber-700"
                  />
                </div>
                <h3 className="text-2xl font-semibold text-slate-800 mb-4 group-hover:text-amber-700 transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section - Zaman Çizelgesi */}
      <section className="py-24 bg-white relative" ref={refProcess}>
        {/* Dekoratif Çizgiler */}
        <div className="absolute inset-0 flex justify-center">
          <div className="w-px h-full bg-gradient-to-b from-transparent via-amber-200 to-transparent opacity-70 hidden md:block"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center mb-20"
            initial="hidden"
            animate={controlsProcess}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">NASIL ÇALIŞIYORUZ?</span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6">
              Üretim Sürecimiz
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              En son teknoloji baskı sistemlerimiz ve uzman ekibimizle, 
              her ürünümüzü özenle üretiyoruz.
            </p>
          </motion.div>

          <motion.div 
            className="relative max-w-5xl mx-auto"
            initial="hidden"
            animate={controlsProcess}
            variants={staggerContainer}
          >
            {processSteps.map((process, index) => (
              <motion.div
                key={index}
                className={`flex items-center mb-16 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                variants={itemFadeIn}
              >
                <div className={`hidden md:flex w-1/2 ${index % 2 === 0 ? 'justify-end pr-12' : 'justify-start pl-12'}`}>
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-100 rounded-2xl transform rotate-3"></div>
                    <div className="relative bg-white shadow-lg rounded-2xl p-6 border border-slate-100 w-full max-w-sm">
                      <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
                        {process.step}
                      </div>
                      <FontAwesomeIcon icon={process.icon} className="text-3xl text-amber-700 mb-4" />
                      <h3 className="text-xl font-semibold text-slate-800 mb-3">{process.title}</h3>
                      <p className="text-slate-600">{process.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Mobil Görünüm */}
                <div className="md:hidden w-full">
                  <div className="relative bg-white shadow-lg rounded-2xl p-6 border border-slate-100">
                    <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
                      {process.step}
                    </div>
                    <FontAwesomeIcon icon={process.icon} className="text-3xl text-amber-700 mb-4" />
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{process.title}</h3>
                    <p className="text-slate-600">{process.description}</p>
                  </div>
                </div>
                
                {/* Orta Nokta */}
                <div className="hidden md:flex w-10 h-10 rounded-full bg-amber-600 border-4 border-white shadow-md z-10 mx-auto"></div>
                
                <div className={`hidden md:flex w-1/2 ${index % 2 === 0 ? 'justify-start pl-12' : 'justify-end pr-12'}`}>
                  {/* Boş alan - zaman çizelgesi için */}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section - Modern Kartlar */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative" ref={refContact}>
        {/* Dekoratif Şekiller */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-amber-100 opacity-30 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-amber-200 opacity-20 rounded-tr-full"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center mb-20"
            initial="hidden"
            animate={controlsContact}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">İLETİŞİM</span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6">
              Bize Ulaşın
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Sorularınız veya önerileriniz için bizimle iletişime geçebilirsiniz.
              Size en kısa sürede dönüş yapacağız.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            initial="hidden"
            animate={controlsContact}
            variants={staggerContainer}
          >
            <motion.div
              className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
              variants={itemFadeIn}
            >
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faPhone} className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Telefon</h3>
              <p className="text-slate-600 mb-4">Hafta içi 09:00 - 18:00</p>
              <a href="tel:+905551234567" className="text-amber-700 font-medium hover:text-amber-800 transition-colors duration-300">
                +90 (535) 873 46 92
              </a>
            </motion.div>

            <motion.div
              className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
              variants={itemFadeIn}
            >
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faEnvelope} className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">E-posta</h3>
              <p className="text-slate-600 mb-4">24 saat içinde dönüş</p>
              <a href="mailto:mr.browncases@gmail.com" className="text-amber-700 font-medium hover:text-amber-800 transition-colors duration-300">
                mr.browncases@gmail.com
              </a>
            </motion.div>

            <motion.div
              className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
              variants={itemFadeIn}
            >
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Adres</h3>
              <p className="text-slate-600 mb-4">Merkez Ofisimiz</p>
              <p className="text-amber-700 font-medium">
                Kepez, Antalya, Türkiye
              </p>
            </motion.div>
          </motion.div>

          
          {/* CTA Butonu */}
          <div className="text-center mt-16">
            <motion.a
              href="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Bizimle İletişime Geçin
            </motion.a>
          </div>
        </div>
      </section>
      
      {/* Özel CSS Animasyonları için stil */}
      <style jsx>{`
        .perspective {
          perspective: 1000px;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default About; 